import { useEffect, useState } from 'react';
import { api } from '@/api/client';

interface InvitePreviewModalProps {
  vacancyId: string;
  onClose: () => void;
}

interface Preview {
  candidate_id: string;
  candidate_name: string;
  message: string;
  landing_url: string;
  personal_questions: number;
}

interface PreviewData {
  approved: boolean;
  pending_total: number;
  previews: Preview[];
  template: string;
}

export default function InvitePreviewModal({ vacancyId, onClose }: InvitePreviewModalProps) {
  const [data, setData] = useState<PreviewData | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get<PreviewData>(`/vacancies/${vacancyId}/invite-preview`)
      .then(setData)
      .catch((e) => setError((e as Error).message || 'Не удалось получить предпросмотр'));
  }, [vacancyId]);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto p-4 md:p-8" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-3xl shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-background-200">
          <div>
            <h3 className="text-base font-heading font-semibold text-foreground-900">Письмо глазами кандидата</h3>
            <p className="text-xs text-foreground-500 mt-0.5">
              Тот же текст, что уйдёт при рассылке — с подставленным именем и личной ссылкой
            </p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-background-100 flex items-center justify-center cursor-pointer" aria-label="Закрыть">
            <i className="ri-close-line text-foreground-500" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4 max-h-[65vh] overflow-y-auto">
          {error && <div className="p-3 rounded-xl bg-red-50 border border-red-100 text-red-700 text-sm">{error}</div>}
          {!data && !error && <p className="text-sm text-foreground-400">Загружаю…</p>}

          {data && (
            <>
              <div className={`p-3 rounded-xl text-sm border ${data.approved ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
                {data.approved
                  ? `Письмо утверждено. Ждут отправки: ${data.pending_total} — уйдут при ближайшей синхронизации (каждые 15 минут).`
                  : `Письмо не утверждено, рассылка стоит. Как только утвердите — письма уйдут ${data.pending_total} кандидатам.`}
              </div>

              {data.previews.length === 0 && (
                <p className="text-sm text-foreground-500">
                  Кандидатов, ожидающих письма, нет — показывать не на ком.
                </p>
              )}

              {data.previews.map((p) => (
                <div key={p.candidate_id} className="bg-background-50 rounded-xl border border-background-200 p-4">
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <span className="text-sm font-medium text-foreground-900">{p.candidate_name}</span>
                    <span className={`text-[11px] px-2 py-0.5 rounded-full ${p.personal_questions ? 'bg-primary-50 text-primary-700' : 'bg-background-200 text-foreground-500'}`}>
                      {p.personal_questions
                        ? `в анкете +${p.personal_questions} вопроса по его резюме`
                        : 'только общие вопросы'}
                    </span>
                  </div>
                  <pre className="text-sm text-foreground-800 whitespace-pre-wrap font-sans leading-relaxed">
                    {p.message}
                  </pre>
                  <a
                    href={p.landing_url}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-3 inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-background-300 text-foreground-600 hover:bg-background-100 transition-colors"
                  >
                    <i className="ri-external-link-line" />
                    Открыть его анкету
                  </a>
                </div>
              ))}

              <p className="text-xs text-foreground-500">
                Подстановки в тексте письма: <code>{'{name_patronymic}'}</code> — имя и отчество,{' '}
                <code>{'{first_name}'}</code> — только имя, <code>{'{candidate_name}'}</code> — имя и фамилия,{' '}
                <code>{'{landing_url}'}</code> — личная ссылка на анкету.
              </p>
            </>
          )}
        </div>

        <div className="px-6 py-4 border-t border-background-200 flex justify-end">
          <button onClick={onClose} className="px-4 py-2.5 rounded-xl text-sm text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer">
            Закрыть
          </button>
        </div>
      </div>
    </div>
  );
}
